<template>
  <div class="default-layout-container" id="pathways-explorer">
    <div class="content-container-1140">
      <div
        v-if="'data' in explorerInfo && 'attributes' in explorerInfo.data"
        class="page-content md-content"
      >
        <v-container fluid>
          <v-row no-gutters>
            <v-col cols="12" sm="9">
              <h1 class="mb-0">{{ $t(`navigation.routeTitles.explore`) }}</h1>
            </v-col>
            <v-col cols="12" sm="3">
              <v-autocomplete
                v-model="selectedRegion"
                :items="regionsArray"
                :item-title="'name'"
                :item-value="'id'"
                menu-icon="$chevronDown"
                @update:model-value="updateRegion"
                variant="outlined"
              ></v-autocomplete>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12">
              <h2 class="mt-0 pt-0">
                {{ regions[selectedRegion].name }}
                {{
                  selectedRegion === "SA" ? $t(`explorer.regionalOverview`) : ""
                }}
                <v-chip v-if="regionHasPledge" class="ml-8"
                  ><v-icon icon="$leafSolid" class="mr-2"></v-icon>
                  {{ $t("explorer.methanePledge") }}</v-chip
                >
              </h2>
              <div
                v-if="'data' in countryIntro && countryIntro.data.length"
                class="page-content md-content"
                v-html="
                  md.render(countryIntro.data[0]?.attributes.CountrySummaryHTML)
                "
              ></div>
            </v-col>
          </v-row>
          <v-row>
            <!-- SCENARIO CONTROLS -->
            <v-col xs="12" sm="6" md="4">
              <div
                class="switches-container scrollable-container px-5 px-md-12 py-6 elevation-3"
              >
                <v-row>
                  <v-col cols="12">
                    <h4 class="text-center">
                      {{ t("explorer.mitigationMeasures") }}
                    </h4>
                  </v-col>
                </v-row>
                <div v-for="sector in sectors">
                  <v-row no-gutters>
                    <v-col cols="12">
                      <v-switch
                        class="v-input--reverse v-input--expand mt-3 mb-0 py-0"
                        density="compact"
                        v-model="scenarioSectors"
                        :value="sector.id"
                        :color="sector.colorAccent"
                        hide-details
                      >
                        <template v-slot:label>
                          <h5>
                            <v-icon
                              :icon="`$${sector.icon}`"
                              class="mr-2"
                            ></v-icon
                            >{{ sector.name }}
                          </h5>
                        </template>
                      </v-switch>
                    </v-col>
                  </v-row>
                  <v-expand-transition>
                    <v-row
                      v-show="scenarioSectors.includes(sector.id)"
                      no-gutters
                    >
                      <v-col
                        v-for="solution in getSolutionsForSector(sector.id)"
                        cols="12"
                        class="mb-2"
                      >
                        <v-switch
                          class="v-input--reverse v-input--expand"
                          density="compact"
                          v-model="scenarioSolutions"
                          :value="solution.id"
                          :color="sector.colorAccent"
                          hide-details
                        >
                          <template v-slot:label>
                            {{ solution.name }}
                          </template>
                        </v-switch>
                      </v-col>
                    </v-row>
                  </v-expand-transition>
                </div>
                <v-divider></v-divider>
                <v-row no-gutters="">
                  <v-col class="py-2">
                    <h5>{{ $t("explorer.implementationRates.title") }}</h5>
                    <v-slider
                      :ticks="implementationRates"
                      v-model="scenarioSolutionRate"
                      max="2"
                      step="1"
                      show-ticks="always"
                      tick-size="4"
                    ></v-slider>
                  </v-col>
                </v-row>
                <v-divider></v-divider>
                <v-row no-gutters="">
                  <v-col>
                    <h5 class="mt-2">{{ $t("explorer.playbackControls") }}</h5>
                    <playback-slider
                      @update:year="playbackUpdateYear"
                    ></playback-slider>
                  </v-col>
                </v-row>
              </div>
            </v-col>
            <!-- END SCENARIO CONTROLS -->
            <!-- SCENARIO DATA... CHARTS AND SO ON -->
            <v-col xs="12" sm="6" md="8">
              <div class="data-container pa-4">
                <!-- METHANE EMISSIONS BAR CHARTS -->
                <h3 v-if="selectedRegion === 'SA'">
                  {{ t("explorer.methaneEmissionsByCountry") }} -
                  {{ selectedYear }}
                </h3>
                <h3 v-else>
                  {{ regions[selectedRegion].name }}
                  {{ t("explorer.methaneEmissions") }} -
                  {{ selectedYear }}
                </h3>
                <!-- MAP SECTION -->
                <mapbox-map
                  container="map"
                  :year="selectedYear"
                  :highlight-region-id="selectedRegion"
                  ref="map"
                  @update:region="updateRegion"
                ></mapbox-map>
                <!-- END MAP SECTION -->
                <div v-if="selectedRegion === 'SA'">
                  <div v-if="scenarioReady">
                    <h4 class="text-center mt-12 mb-0 pb-0">
                      {{ t("explorer.methaneEmissions") }}
                      <span class="text-subtitle-2">(ktCH4)</span>
                    </h4>
                    <pathways-bar-chart
                      :chart-data="chartDataEmissionsByCountryForSelectedYear"
                      chart-units="ktCH4"
                      :suggested-max="scenarios.bau.data.BR.total.ch4[30]"
                    ></pathways-bar-chart>
                    <h4 class="text-center mt-12 mb-0 pb-0">
                      {{ t("explorer.methaneEmissionsPerCapita") }}
                      <span class="text-subtitle-2">{{
                        `(tCH4/${t("explorer.person")})`
                      }}</span>
                    </h4>
                    <pathways-bar-chart
                      :chart-data="
                        chartDataPerCapitaEmissionsByCountryForSelectedYear
                      "
                      :chart-units="`tCH4/${t('explorer.person')}`"
                      :suggested-max="
                        (scenarios.bau.data.UY.total.ch4[30] * 1000) /
                        regions.UY.population[30]
                      "
                    ></pathways-bar-chart>
                  </div>
                </div>
                <div v-if="scenarioReady">
                  <h4 class="text-center mt-12 mb-0 pb-0">
                    {{ t("explorer.methaneEmissionsBySector") }}
                    <span class="text-subtitle-2">(ktCH4)</span>
                  </h4>
                  <pathways-bar-chart
                    :chart-data="chartDataEmissionsBySectorForSelectedYear"
                    chart-units="ktCH4"
                    :suggested-max="
                      scenarios.bau.data[selectedRegion].agriculture.ch4[30]
                    "
                  ></pathways-bar-chart>
                </div>
                <div v-if="scenarioReady">
                  <h4 class="text-center mt-12 mb-0 pb-0">
                    {{ t("explorer.methaneReductions") }}
                    <span class="text-subtitle-2">(ktCH4)</span>
                  </h4>
                  <pathways-budget-line-chart
                    :chart-data="chartDataReductions"
                    chart-units="ktCH4"
                    :suggested-max="
                      scenarios.bau.data[selectedRegion].total.ch4[30]
                    "
                    :suggested-min="
                      scenarios.hhh.data[selectedRegion].total.ch4[30]
                    "
                    :highlight-year="selectedYear"
                    :aspect-ratio="1"
                    :mobile-aspect-ratio="0.4"
                    stacked
                    class="mt-n8"
                  ></pathways-budget-line-chart>
                </div>
                <!-- END METHANE EMISSIONS BAR CHARTS -->
                <!-- METHANE BUDGET SECTION -->
                <h3 class="mt-12">
                  {{ t("explorer.methaneBudget") }} -
                  {{ selectedRegion === "SA" ? t("explorer.allOf") : "" }}
                  {{ regions[selectedRegion].name }}
                </h3>
                <div
                  v-html="
                    md.render(
                      explorerInfo.data?.attributes
                        .UnderstandingMethaneBudgetHTML
                    ).replace('{CountryName}', regions[selectedRegion].name).replace('{Country1.5Budget}', `${budgets.C1.data[selectedRegion].ch4[29].toLocaleString()} ktCH4/${t('explorer.yearAbbr')}`)
                  "
                ></div>
                <h4 class="text-center mt-12 mb-0 pb-0">
                  {{ t("explorer.methaneBudget") }}
                  <span class="text-subtitle-2">(ktCH4)</span>
                </h4>
                <h5 class="my-0 py-0">
                  {{ t("explorer.referencePathway") }}
                  <v-autocomplete
                    v-model="selectedBudget"
                    :items="budgetsArray"
                    :item-title="'description'"
                    :item-value="'id'"
                    menu-icon="$chevronDown"
                    variant="outlined"
                    class="mb-n8 pb-0"
                  ></v-autocomplete>
                </h5>
                <div v-if="scenarioReady">
                  <pathways-budget-line-chart
                    :chart-data="chartDataMethaneBudget"
                    chart-units="ktCH4"
                    :suggested-max="budgets.C8.data[selectedRegion].ch4[29]"
                    :min="0"
                    :highlight-year="selectedYear"
                  ></pathways-budget-line-chart>
                </div>
                <!-- END METHANE BUDGET SECTION -->
                <!-- COSTS AND BENEFITS -->
                <!-- financial costs benefits -->
                <h3 class="mt-12">
                  {{ t("explorer.financialCost") }} -
                  {{ selectedRegion === "SA" ? t("explorer.allOf") : "" }}
                  {{ regions[selectedRegion].name }}
                </h3>
                <div
                  v-html="
                    md.render(
                      explorerInfo.data?.attributes.FinancialCostBenefitHTML
                    )
                  "
                ></div>
                <div v-if="scenarioReady">
                  <h4 class="text-center mt-12 mb-0 pb-0">
                    {{ t("explorer.financialCost") }}
                    <span class="text-subtitle-2">{{
                      `(${t("explorer.millionUSD")})`
                    }}</span>
                  </h4>
                  <pathways-bar-chart
                    :chart-data="chartDataFinancialCostSaving"
                    :chart-units="t('explorer.millionUSD')"
                    :suggested-max="
                      scenarios.mmm.data[selectedRegion].total.cost
                    "
                    stacked
                    column
                  ></pathways-bar-chart>
                </div>
                <!-- end financial costs benefits -->
                <!-- social costs benefits -->
                <h3 class="mt-12">
                  {{ t("explorer.socialCost") }} -
                  {{ selectedRegion === "SA" ? t("explorer.allOf") : "" }}
                  {{ regions[selectedRegion].name }}
                </h3>
                <div
                  v-html="
                    md.render(
                      explorerInfo.data?.attributes.SocialCostBenefitHTML
                    )
                  "
                ></div>
                <div v-if="scenarioReady">
                  <h4 class="text-center mt-12 mb-0 pb-0">
                    {{ t("explorer.remainingAvoidedEmissions") }}
                    <span class="text-subtitle-2">{{
                      `(ktCH4/${t("explorer.year")})`
                    }}</span>
                  </h4>
                  <pathways-bar-chart
                    :chart-data="chartDataAvoidedEmissions"
                    chart-units="ktCH4"
                    :suggested-max="
                      scenarios.bau.data[selectedRegion].total.ch4[30]
                    "
                    stacked
                    column
                  ></pathways-bar-chart>
                </div>
                <div>
                  <h4 class="mt-8 mb-0 text-center">
                    {{ t("explorer.socialCases.title") }} {{ startYear }} -
                    {{ selectedYear }}
                  </h4>
                  <v-table density="compact">
                    <thead>
                      <tr>
                        <th class="text-left font-weight-bold">
                          {{ t("explorer.sector") }}
                        </th>
                        <th
                          v-for="item in socialCaseTypesArray"
                          :key="item.id"
                          class="text-center font-weight-bold"
                        >
                          {{ item.name }}
                        </th>
                      </tr>
                      <tr></tr>
                    </thead>
                    <tbody>
                      <template
                        v-for="sector in [...sectorCodes, 'total']"
                        :key="sector"
                      >
                        <v-slide-y-transition>
                          <tr
                            v-show="
                              socialBenefitsVals[sector].total[1][
                                selectedYear - startYear
                              ] !== 0 || sector === 'total'
                            "
                            :class="`${sector}-benefits`"
                          >
                            <td class="text-left font-weight-medium">
                              {{
                                sector !== "total"
                                  ? t(`explorer.sectors.${sector}.name`)
                                  : t("explorer.total")
                              }}
                            </td>
                            <td
                              v-for="(item, index) in socialCaseTypesArray"
                              :key="`${sector}${item}`"
                              class="text-center"
                            >
                              {{
                                socialBenefitsVals[sector][item.id][1][
                                  selectedYear - startYear
                                ].toLocaleString(locale)
                              }}
                              <br />
                              <span class="text-caption">
                                ({{
                                  socialBenefitsVals[sector][item.id][0][
                                    selectedYear - startYear
                                  ].toLocaleString(locale)
                                }}/{{ t("explorer.yearAbbr") }})
                              </span>
                            </td>
                          </tr>
                        </v-slide-y-transition>
                      </template>
                    </tbody>
                  </v-table>
                </div>
                <div v-if="scenarioReady">
                  <h4 class="text-center mt-12 mb-0 pb-0">
                    {{ t("explorer.monetizedSocialBenefits") }}
                    <span class="text-subtitle-2">{{
                      `(${t("explorer.millionUSD")})`
                    }}</span>
                  </h4>
                  <pathways-bar-chart
                    :chart-data="chartDataBenefitsBySector"
                    :chart-units="t('explorer.millionUSD')"
                    :suggested-max="
                      scenarios.hhh.data[selectedRegion].agriculture.benefit
                    "
                    column
                  ></pathways-bar-chart>
                </div>
                <!-- end social costs benefits -->
                <!-- END COSTS AND BENEFITS -->
              </div>
            </v-col>
            <!-- END SCENARIO DATA... CHARTS AND SO ON -->
          </v-row>
        </v-container>
      </div>
    </div>
  </div>
</template>
<script lang="ts" setup>
const { locale, t } = useI18n();
const localePath = useLocalePath();
const { md } = useMarkdownIt();
const route = useRoute();
const router = useRouter();
const {
  regions,
  regionsArray,
  regionCodes,
  countryCodes,
  sectors,
  sectorCodes,
  sectorLocaleSlugs,
  sectorLocaleXRefs,
  getSolutionsForSector,
  budgets,
  budgetsArray,
  scenarios,
  startYear,
  currentYear,
  commitmentYear,
  endYear,
  socialCaseTypesArray,
} = usePathwaysConfig();
const debounce = genericUtils().debounce;

// ref for the mapbox map
const map = ref(null);

// api content
const contentType = ["countries", "explorer"];
const key = [
  `${contentType[0]}_${locale.value}`,
  `${contentType[1]}_${locale.value}`,
];

const apiURL = [
  getStrapiLink(contentType[0], {
    fields: ["CountryCode", "CountrySummaryHTML"],
    filters: {
      CountryCode: {
        $eqi: ["all", "todos"].includes(route.params.country.toLowerCase())
          ? "SA"
          : route.params.country,
      },
    },
  }),
  getStrapiLink(contentType[1], {
    fields: [
      "UnderstandingMethaneBudgetHTML",
      "FinancialCostBenefitHTML",
      "SocialCostBenefitHTML",
    ],
  }),
];

// when multiple useFetch calls are required, they need to be put inside a Promise.all() (see https://github.com/nuxt/nuxt/issues/14725#issuecomment-1397364309 example)
const [{ data: countryIntro }, { data: explorerInfo }] = await Promise.all([
  useFetch(apiURL[0], {
    key: key[0],
    watch: false,
  }),
  useFetch(apiURL[1], {
    key: key[1],
    watch: false,
  }),
]);

// track pathway settings
const selectedRegion = ref("SA"); // track which region data is being filtered to for charts
const selectedSector = ref(); // track which sector data is being filtered to for charts
const selectedBudget = ref("C1"); // track which pathway/budget to compare against

onBeforeMount(() => {
  // PARSE PARAMS

  /**
   * checks if supplied sector param is valid.
   * if not valid, URL is updated to 'all'
   */
  const checkSectorIsValid = () => {
    // check if sector is valid
    if (route.params.sector !== "") {
      if (sectorLocaleSlugs.includes(route.params.sector)) {
        selectedSector.value = sectorLocaleXRefs[route.params.sector];
      } else {
        // invalid sector
        selectedSector.value = sectorLocaleXRefs[t("explorer.all")];
        router.replace(
          route.fullPath.slice(0, -(route.params.sector.length + 1))
        );
      }
    }
  };

  /**
   * replaces the region portion of the URL if the supplied region param is not a valid region
   * if region is not valid, url is updated to 'SA'
   */
  const replaceInvalidRegion = () => {
    const paramsPathPart: string = Object.values(route.params).reduce(
      (acc, curr) => {
        return curr !== "" ? acc + "/" + curr : acc;
      }
    ) as string;
    selectedRegion.value = "SA";
    router.replace(
      `${route.fullPath.replace(paramsPathPart, "")}SA/${t("explorer.all")}`
    );
  };
  // check if region is valid
  if (route.params.region !== "SA") {
    // only allowable value is SA... empty or other values should redirect
    replaceInvalidRegion();
  } else {
    // check if country is valid
    if (!["", t("explorer.all")].includes(route.params.country)) {
      if (
        regionCodes.includes(route.params.country) &&
        route.params.country !== "SA"
      ) {
        selectedRegion.value = route.params.country;
        checkSectorIsValid();
      } else {
        // invalid country
        replaceInvalidRegion();
      }
    } else {
      selectedRegion.value = "SA";
      checkSectorIsValid();
    }
  }
  // init sector if it's not set
  if (selectedSector.value == undefined) {
    selectedSector.value = "all";
  }

  // page head title
  let headTitle = `${t("navigation.routeTitles.explore")} - ${t("explorer.regions." + selectedRegion.value + ".name")}`;
  if (selectedSector.value !== "all") {
    headTitle += ` - ${t("explorer.sectors." + selectedSector.value + ".name")}`
  }
  useHead({
    title: headTitle
  });
});

/**
 * updates the selected sector. URL is updated to reflect the updated sector
 */
const updateSector = (id: string) => {
  selectedSector.value = id;
  const slug = t(`explorer.sectors.${id}.slug`);
  let newUrl = route.params.sector.length
    ? route.fullPath.slice(0, -(route.params.sector.length + 1))
    : route.fullPath;
  newUrl = newUrl.endsWith("/") ? newUrl + slug : newUrl + "/" + slug;
  navigateTo(newUrl);
};

const regionHasPledge = computed(() => {
  return regions[selectedRegion.value].hasCH4Pledge === true;
});

/**
 * updates the selected region. URL is updated to reflect the updated region
 * default is to update based on the selectedRegion ref if no regionId is provided in function call
 */
const updateRegion = (regionId = null) => {
  if (regionId === null) {
    regionId = selectedRegion.value;
  }
  const newRouteParams = {
    region: "SA",
    country:
      regionId === "SA"
        ? t(`explorer.all`)
        : regionId,
  };
  if (![null, "all"].includes(selectedSector.value)) {
    newRouteParams.sector = t(`explorer.sectors.${selectedSector.value}.slug`);
  }
  const newRoute = localePath({
    name: "explore-region-country-sector",
    params: newRouteParams,
  });
  navigateTo(newRoute, { external: true }); // force full page refresh to make sure new country data is loaded for production SSR pages
};

// scenario settings / mitigation measures controls
/**
 * returns the currently selected scenario based on implementation rate and selected sectors in scenario controls
 */
const selectedScenario = computed(() => {
  if (scenarioSectors.value.length > 0) {
    // not bau
    let currentScenario = "";
    const implementationRateCodes = ["l", "m", "h"];
    const currRate = implementationRateCodes[scenarioSolutionRate.value];
    sectorCodes.forEach((sector) => {
      if (scenarioSectors.value.includes(sector)) {
        currentScenario += currRate; // sector is on
      } else {
        currentScenario += "o"; // sector is off
      }
    });
    const newScenario = JSON.parse(JSON.stringify(scenarios[currentScenario]));
    // newScenario automatically assumes all solutions are turned on...
    return newScenario;
  } else {
    // bau
    return scenarios.bau;
  }
});

const scenarioReady = ref(false); // don't display charts until scenario data is ready
const scenarioSectors = ref([]); // track which sectors are activated for scenario solutions - this is independent from the selectedSector
const scenarioSolutions = ref([]); // track which solutions are activated
const implementationRates = ref({
  // implementation rates to display on scenario controls
  0: t("explorer.implementationRates.low"),
  1: t("explorer.implementationRates.med"),
  2: t("explorer.implementationRates.high"),
});
const scenarioSolutionRate = ref(0); // the selected rate

// watch for scenario updates
watch(selectedScenario, () => {
  updateAllCharts();
});

// wire scenarioSolutions switches to scenarioSectors switches
watch(scenarioSectors, (newVal, oldVal) => {
  sectorCodes.forEach((sector) => {
    if (newVal.includes(sector) && !oldVal.includes(sector)) {
      getSolutionsForSector(sector).forEach((solution) => {
        scenarioSolutions.value.push(solution.id);
      });
    }
    if (oldVal.includes(sector) && !newVal.includes(sector)) {
      getSolutionsForSector(sector).forEach((solution) => {
        scenarioSolutions.value = scenarioSolutions.value.filter(
          (item) => item !== solution.id
        );
      });
    }
  });
});

watch(scenarioSolutions, (newVal, oldVal) => {
  const addedSolutions = newVal.filter(
    (solution) => !oldVal.includes(solution)
  );
  const removedSolutions = oldVal.filter(
    (solution) => !newVal.includes(solution)
  );
  addedSolutions.forEach((solution) => {
    // reduce the emissions
    updateSolutionReductions(solution, "subtract");
  });
  removedSolutions.forEach((solution) => {
    // increase the emissions
    updateSolutionReductions(solution, "add");
  });
});

watch(selectedBudget, () => {
  chartDataMethaneBudget.value = setChartDataMethaneBudget();
});

/**
 * updates ch4 emission values for scenario total and sectors for provided scenario object
 */
const updateSolutionReductions = (solution: string, method: string) => {
  /**
   * updates the values related to a solution being added or removed to a country/region in the scenario
   * @param obj scenario sub-object for a region to be updated
   */
  const updateRegionValues = (obj) => {
    if ("solutions" in obj && solution in obj.solutions) {
      // add 0's to front of array
      const solutionData = JSON.parse(JSON.stringify(obj.solutions[solution]));
      solutionData.ch4.unshift(...Array(currentYear - startYear + 1).fill(0));
      // determine the sector to update
      const solutionSector = sectorCodes.find((sector) =>
        sector.startsWith(solution.slice(0, 2))
      );
      // update total
      for (let i = 0; i < obj.total.ch4.length; i++) {
        ["total", solutionSector].forEach((key) => {
          if (method === "add") {
            // increase emissions
            obj[key].ch4[i] = parseFloat(
              (obj[key].ch4[i] + solutionData.ch4[i]).toFixed(1)
            );
            if (i === 0 && "cost" in solutionData) {
              // increase cost
              obj[key].cost = parseFloat(
                (obj[key].cost + solutionData.cost).toFixed(2)
              );
            }
            if (i === 0 && "benefit" in solutionData) {
              // decrease benefit
              obj[key].benefit = parseFloat(
                (obj[key].benefit - solutionData.benefit).toFixed(2)
              );
            }
          } else {
            // decrease emissions
            obj[key].ch4[i] = parseFloat(
              (obj[key].ch4[i] - solutionData.ch4[i]).toFixed(1)
            );
            if (i === 0 && "cost" in solutionData) {
              obj[key].cost = parseFloat(
                (obj[key].cost - solutionData.cost).toFixed(2)
              );
            }
            if (i === 0 && "benefit" in solutionData) {
              obj[key].benefit = parseFloat(
                (obj[key].benefit + solutionData.benefit).toFixed(2)
              );
            }
          }
        });
      }
    }
  };

  updateRegionValues(selectedScenario.value.data[selectedRegion.value]);
  if (selectedRegion.value === "SA") {
    // also update values for all countries if doing a SA regional overview
    countryCodes.forEach((country: string) => {
      updateRegionValues(selectedScenario.value.data[country]);
    });
  }
  updateAllCharts();
};

const selectedYear = ref(commitmentYear); // the current year of data being displayed
const playbackUpdateYear = (newYear: number) => {
  selectedYear.value = newYear;
  updateSingleYearCharts();
};
const yearIndex = computed(() => {
  return selectedYear.value - startYear;
});

const barBgColor = "#247775";
const barHoverBgColor = "#33A390";
const countryLabels = countryCodes.map(
  (region: string) => regions[region].name
) as string[]; // list of country names for labels
const sectorLabels = sectorCodes.map(
  (sector: string) => sectors[sector].name
) as string[]; // list of sector names for chart labels

/**
 * tool for automatically darkening colors
 * used for defining chart line colors
 * @param hex - a hex color value
 * @param amount - the amount to darken the color between 0.0 and 1.0
 */
function darkenColor(hex: string, amount: number) {
  // Parse the hex value into RGB components
  const red = parseInt(hex.slice(1, 3), 16);
  const green = parseInt(hex.slice(3, 5), 16);
  const blue = parseInt(hex.slice(5, 7), 16);

  // Darken the color by the specified amount
  const darkerRed = Math.floor(red * amount);
  const darkerGreen = Math.floor(green * amount);
  const darkerBlue = Math.floor(blue * amount);

  // Convert the darker RGB values back to hex
  const darkerHex =
    "#" +
    ("0" + darkerRed.toString(16)).slice(-2) +
    ("0" + darkerGreen.toString(16)).slice(-2) +
    ("0" + darkerBlue.toString(16)).slice(-2);

  return darkerHex;
}

/**
 * assembles the chartData object required by chart.js
 * @param values the values to go in the dataset
 * @param labels the labels to display
 */
const assembleBarChartDataObj = (
  values: number[],
  labels: string[],
  bgColor = barBgColor,
  bgHoverColor = barHoverBgColor
) => {
  return {
    labels: labels,
    datasets: [
      {
        backgroundColor: bgColor,
        hoverBackgroundColor: bgHoverColor,
        data: values,
      },
    ],
  };
};

/**
 * assemble chart data for emissions by country for selectedYear
 */
const chartDataEmissionsByCountryForSelectedYear = ref({});
const setChartDataEmissionsByCountryForSelectedYear = () => {
  const values: number[] = [];
  countryCodes.forEach((region: string) => {
    values.push(selectedScenario.value.data[region].total.ch4[yearIndex.value]);
  });
  return assembleBarChartDataObj(values, countryLabels);
};

/**
 * assemble chart data for per capita emissions by country for selectedYear
 */
const chartDataPerCapitaEmissionsByCountryForSelectedYear = ref({});
const setChartDataPerCapitaEmissionsByCountryForSelectedYear = () => {
  const values: number[] = [];
  countryCodes.forEach((region: string) => {
    values.push(
      parseFloat(
        (
          (selectedScenario.value.data[region].total.ch4[yearIndex.value] *
            1000) /
          regions[region].population[yearIndex.value]
        ).toFixed(2)
      )
    );
  });
  return assembleBarChartDataObj(values, countryLabels);
};

/**
 * assemble chart data for emissions by sector for region for selectedYear
 */
const chartDataEmissionsBySectorForSelectedYear = ref({});
const setChartDataEmissionsBySectorForSelectedYear = () => {
  const values: number[] = [];
  const bgColors: string[] = [];
  const bgHoverColors: string[] = [];
  sectorCodes.forEach((sector: string) => {
    values.push(
      selectedScenario.value.data[selectedRegion.value][sector].ch4[
        yearIndex.value
      ]
    );
    bgColors.push(sectors[sector].color);
    bgHoverColors.push(sectors[sector].colorAccent);
  });
  return assembleBarChartDataObj(values, sectorLabels, bgColors, bgHoverColors);
};

/**
 * assembles chart data for reductions wedge chart
 */
const chartDataReductions = ref({});
const setChartDataReductions = () => {
  const projectedValues: number[] =
    selectedScenario.value.data[selectedRegion.value].total.ch4;

  const datasets = [
    {
      label: t("explorer.modelledScenario"),
      backgroundColor: "#c94b3b",
      borderColor: "#c94b3b",
      borderWidth: 1,
      hoverBackgroundColor: "#994035",
      data: projectedValues,
      fill: false,
    },
  ];

  // assemble solutions datasets
  const scenSolutions = JSON.parse(JSON.stringify(scenarioSolutions.value));
  scenSolutions.sort(); // order solutions by their sector
  let currSect = "";
  let colorIndex = 0;
  scenarioSolutions.value.forEach((solution) => {
    // assemble solution dataset
    const sector = sectorCodes.find((sect) =>
      sect.startsWith(solution.substring(0, 2))
    );
    if (currSect !== sector) {
      colorIndex = 0;
    } else {
      colorIndex += 1;
    }
    currSect = sector;
    let solutionData = [];
    if (
      solution in selectedScenario.value.data[selectedRegion.value].solutions
    ) {
      solutionData = JSON.parse(
        JSON.stringify(
          selectedScenario.value.data[selectedRegion.value].solutions[solution]
            .ch4
        )
      );
      solutionData.unshift(...Array(currentYear - startYear + 1).fill(0));
    } else {
      solutionData = Array(endYear - startYear).fill(0);
    }

    const ds = {
      label: sectors[sector].solutions[solution].name,
      borderColor: darkenColor(
        sectors[sector].solutionsColors[colorIndex],
        0.5
      ),
      borderWidth: 1,
      backgroundColor: sectors[sector].solutionsColors[colorIndex],
      hoverBackgroundColor: sectors[sector].colorAccent,
      data: solutionData,
      fill: "0",
    };

    datasets.push(ds);
  });

  return {
    labels: Array.from(
      { length: endYear - startYear + 1 },
      (_, index) => startYear + index
    ),
    datasets: datasets,
  };
};

/**
 * assembles chart data for methane budget line chart
 */
const chartDataMethaneBudget = ref({});
const setChartDataMethaneBudget = () => {
  const projectedValues: number[] =
    selectedScenario.value.data[selectedRegion.value].total.ch4;
  const budgetValues: number[] = JSON.parse(
    JSON.stringify(budgets[selectedBudget.value].data[selectedRegion.value].ch4)
  );
  // budgets start in 2021... duplicate 2021 value to 2020 to start of array
  budgetValues.unshift(budgetValues[0]);
  return {
    labels: Array.from(
      { length: endYear - startYear + 1 },
      (_, index) => startYear + index
    ),
    datasets: [
      {
        label: budgets[selectedBudget.value].description,
        backgroundColor: "#247775",
        borderColor: "#33A390",
        borderWidth: 1,
        hoverBackgroundColor: "#247775",
        data: budgetValues,
      },
      {
        label: t("explorer.modelledScenario"),
        backgroundColor: "#c94b3b",
        borderColor: "#994035",
        borderWidth: 1,
        hoverBackgroundColor: "#c94b3b",
        data: projectedValues,
      },
    ],
  };
};

/**
 * assembles chart data for financials chart
 */
const chartDataFinancialCostSaving = ref({});
const setChartDataFinancialCostSaving = () => {
  const datasets = sectorCodes.map((sector) => {
    return {
      label: sectors[sector].name,
      backgroundColor: sectors[sector].color,
      hoverBackgroundColor: sectors[sector].colorAccent,
      data: [
        selectedScenario.value.data[selectedRegion.value][sector].cost,
        -selectedScenario.value.data[selectedRegion.value][sector].saving,
      ],
    };
  });
  return {
    labels: [t("explorer.costPV"), t("explorer.savingsPV")],
    datasets: datasets,
  };
};

/**
 * assembles chart data for social benefits chart
 * chart shows stacked emissions per sector for 3 years (start, current, end)
 */
const chartDataAvoidedEmissions = ref({});
const setChartDataAvoidedEmissions = () => {
  const outputYears = [2020, selectedYear.value];

  // assemble data for scenario plus sector reductions....
  const obj = selectedScenario.value.data[selectedRegion.value];

  const datasets = [
    // unmitigated emissions data
    {
      label: t("explorer.remainingEmissions"),
      backgroundColor: "#247775",
      hoverBackgroundColor: "#050505",
      data: [obj.total.ch4[0], obj.total.ch4[selectedYear.value - startYear]],
    },
  ];

  // calculate sector reductions
  const sectorReductions = {};
  sectorCodes.forEach((sector) => {
    sectorReductions[sector] = Array(endYear - startYear + 1).fill(0);
  });

  if ("solutions" in obj) {
    scenarioSolutions.value.forEach((solution) => {
      // add 0's to front of array since solutions data doesn't start at startYear
      let solutionData = null;
      if (obj.solutions[solution] !== undefined) {
        // load the solution data
        solutionData = JSON.parse(JSON.stringify(obj.solutions[solution]));
        solutionData.ch4.unshift(...Array(currentYear - startYear + 1).fill(0));
      } else {
        // solution has no data for this region & scenario... populate 0's for all of the solution data points
        solutionData = {
          ch4: Array(currentYear - startYear + 1).fill(0),
        };
      }
      // determine the sector to update
      const solutionSector = sectorCodes.find((sector) =>
        sector.startsWith(solution.slice(0, 2))
      );
      // update sectorReductions
      for (let i = 0; i < sectorReductions[solutionSector].length; i++) {
        sectorReductions[solutionSector][i] = parseFloat(
          (sectorReductions[solutionSector][i] + solutionData.ch4[i]).toFixed(1)
        );
      }
    });
  }

  // add sector datasets
  sectorCodes.forEach((sector) => {
    datasets.push({
      label: `${t("explorer.sectorReductions", {
        sector: sectors[sector].name,
      })}`,
      backgroundColor: sectors[sector].color,
      hoverBackgroundColor: sectors[sector].colorAccent,
      // return 3 values (one for each output year for that sector)
      data: [
        sectorReductions[sector][0],
        sectorReductions[sector][selectedYear.value - startYear],
      ],
    });
  });
  return {
    labels: outputYears,
    datasets: datasets,
  };
};

/**
 * assemble monetized social benefits by sector for region
 */
const chartDataBenefitsBySector = ref({});
const setChartDataBenefitsBySector = () => {
  const values: number[] = [];
  const bgColors: string[] = [];
  const bgHoverColors: string[] = [];
  sectorCodes.forEach((sector: string) => {
    values.push(
      selectedScenario.value.data[selectedRegion.value][sector].benefit
    );
    bgColors.push(sectors[sector].color);
    bgHoverColors.push(sectors[sector].colorAccent);
  });
  return assembleBarChartDataObj(values, sectorLabels, bgColors, bgHoverColors);
};

/**
 * updates charts that only show data for a single year - use when selectedYear changes
 */
const updateSingleYearCharts = () => {
  chartDataEmissionsByCountryForSelectedYear.value =
    setChartDataEmissionsByCountryForSelectedYear();
  chartDataPerCapitaEmissionsByCountryForSelectedYear.value =
    setChartDataPerCapitaEmissionsByCountryForSelectedYear();
  chartDataEmissionsBySectorForSelectedYear.value =
    setChartDataEmissionsBySectorForSelectedYear();
  chartDataAvoidedEmissions.value = setChartDataAvoidedEmissions();
};

/**
 * updates all charts/maps on the dashboard
 */
const updateAllCharts = () => {
  debounceUpdateAllCharts();
};

/**
 * updateAllCharts can get times many times in short succession due to for loops through toggle switches
 * debounce to prevent too many calls
 */
const debounceUpdateAllCharts = debounce(function () {
  chartDataEmissionsByCountryForSelectedYear.value =
    setChartDataEmissionsByCountryForSelectedYear();
  chartDataPerCapitaEmissionsByCountryForSelectedYear.value =
    setChartDataPerCapitaEmissionsByCountryForSelectedYear();
  chartDataEmissionsBySectorForSelectedYear.value =
    setChartDataEmissionsBySectorForSelectedYear();
  chartDataReductions.value = setChartDataReductions();
  chartDataMethaneBudget.value = setChartDataMethaneBudget();
  chartDataFinancialCostSaving.value = setChartDataFinancialCostSaving();
  chartDataAvoidedEmissions.value = setChartDataAvoidedEmissions();
  getSocialBenefitsVals();
  chartDataBenefitsBySector.value = setChartDataBenefitsBySector();
  scenarioReady.value = true;
  if (map.value !== null) {
    map.value.updateMapCH4Data(selectedScenario.value.data);
    if (selectedRegion.value !== "SA") {
      map.value.highlightMapRegion(selectedRegion.value);
    }
  }
}, 100);

/**
 * social cases data...
 */

/**
 * assembles table data for social benefits table
 * table shows each sector contains 2 arrays of values: annual and cumulative
 */
/** sample emptyCaseTypesValsObj
 *
 agriculture: {
   cvd: [[],[]],
   heat: [[],[]],
   respiratory: [[],[]],
   total: [[],[]],
 },
 energy: {
   cvd: [[],[]],
   heat: [[],[]],
   respiratory: [[],[]],
   total: [[],[]],
 },
 waste: {
   cvd: [[],[]],
   heat: [[],[]],
   respiratory: [[],[]],
   total: [[],[]],
 },
 total: {
   cvd: [[],[]],
   heat: [[],[]],
   respiratory: [[],[]],
   total: [[],[]],
 },
 */
const emptyCaseTypesValsObj = {};
[...sectorCodes, "total"].forEach((sector) => {
  emptyCaseTypesValsObj[sector] = {};
  socialCaseTypesArray.forEach((caseType) => {
    emptyCaseTypesValsObj[sector][caseType.id] = [
      Array(endYear - startYear + 1).fill(0),
      Array(endYear - startYear + 1).fill(0),
    ];
  });
});
const socialBenefitsVals = ref(emptyCaseTypesValsObj);

const resetSocialBenefitsVals = () => {
  socialBenefitsVals.value = JSON.parse(JSON.stringify(emptyCaseTypesValsObj));
};

// assemble table data
const getSocialBenefitsVals = () => {
  resetSocialBenefitsVals();

  const obj = selectedScenario.value.data[selectedRegion.value];

  const caseIds = socialCaseTypesArray.map((caseType) => caseType.id);
  // reset cross-sector totals to 0
  caseIds.forEach((caseType) => {
    socialBenefitsVals.value.total[caseType.id] = [
      Array(endYear - startYear + 1).fill(0),
      Array(endYear - startYear + 1).fill(0),
    ];
  });

  if ("solutions" in obj) {
    // get values for each solution and add them to the appropriate sector/case values
    scenarioSolutions.value.forEach((solution) => {
      caseIds.forEach((caseType) => {
        let solutionData = null;
        if (obj.solutions[solution] !== undefined) {
          // gather solution case data
          solutionData = JSON.parse(
            JSON.stringify(obj.solutions[solution].cases[caseType])
          );
          // add 0's to front of array since solutions data doesn't start at startYear
          solutionData.unshift(...Array(currentYear - startYear + 1).fill(0));
        } else {
          // solution has no data for this region & scenario... populate 0's for all of the solution data points
          solutionData = Array(endYear - startYear + 1).fill(0);
        }
        // determine the sector to update
        let solutionSector = sectorCodes.find((sector) =>
          sector.startsWith(solution.slice(0, 2))
        );

        // update socialBenefitsVals cumulatives and cross-sector totals
        for (let i = 0; i <= endYear - startYear; i++) {
          // set annual vals
          socialBenefitsVals.value[solutionSector][caseType][0][i] +=
            solutionData[i];
          // annual cross-sector totals
          socialBenefitsVals.value.total[caseType][0][i] += solutionData[i];

          // case type cumulatives
          socialBenefitsVals.value[solutionSector][caseType][1][i] =
            i === 0
              ? socialBenefitsVals.value[solutionSector][caseType][0][i]
              : socialBenefitsVals.value[solutionSector][caseType][0][i] +
                socialBenefitsVals.value[solutionSector][caseType][1][i - 1];
          // cross-sector total cumulatives
          socialBenefitsVals.value.total[caseType][1][i] =
            i === 0
              ? socialBenefitsVals.value.total[caseType][0][i]
              : socialBenefitsVals.value.total[caseType][0][i] +
                socialBenefitsVals.value.total[caseType][1][i - 1];
        }
      });
    });
  }
};

onMounted(() => {
  // set selected solutions & switches for sector if set
  // do this check on client to make sure switches get triggered
  if (process.client && selectedSector.value !== "all") {
    scenarioSectors.value.push(selectedSector.value);
    getSolutionsForSector(selectedSector.value).forEach((solution) => {
      scenarioSolutions.value.push(solution.id);
    });
  }
  updateAllCharts();
});
</script>

<style lang="scss" scoped>
:deep(*) {
  // expand switch to its container
  .v-input--expand .v-selection-control {
    // justify-content: space-between;
    .v-label {
      display: flex;
      flex: 1;
      padding-top: 0;
      margin-left: 10px;
      margin-right: 0px;
      width: 100%;
    }
  }
}

:deep(.v-field__outline__start) {
  border-top-left-radius: 0 !important;
  border-bottom-left-radius: 0 !important;
}

:deep(.v-field__outline__end) {
  border-top-right-radius: 0 !important;
  border-bottom-right-radius: 0 !important;
}

// Switches Container
.switches-container {
  background-color: #e8f7f4;
  position: sticky;
  top: 120px;

  @media (min-width: 1025px) {
    top: 200px;
  }

  h4 {
    color: #247775;
  }

  hr.v-divider {
    max-width: 100%;
    border-color: #050505;
    opacity: 1;
  }

  :deep(.svg-inline--fa) {
    width: 20px;
    height: 20px;
  }
}

.scrollable-container {
  position: sticky;

  @media (min-width: 600px) {
    overflow-y: auto;
    max-height: calc(100vh - 140px);
  }

  @media (min-width: 1025px) {
    max-height: calc(100vh - 240px);
  }
}

// map container
#map {
  height: 500px;
}

// social benefits table
.agriculture-benefits {
  background-color: rgba(51, 163, 144, 1);
}
.agriculture-benefits:hover {
  background-color: rgba(38, 122, 108, 1);
}
.energy-benefits {
  background-color: rgba(242, 216, 106, 1);
}
.energy-benefits:hover {
  background-color: rgba(182, 162, 80, 1);
}
.waste-benefits {
  background-color: rgba(201, 75, 59, 1);
  color: #fff;
}
.waste-benefits:hover {
  background-color: rgba(153, 64, 53, 1);
  color: #fff;
}
.total-benefits {
  background-color: rgba(232, 247, 244, 0.5);
}
.total-benefits:hover {
  background-color: rgba(232, 247, 244, 1);
}
</style>
